import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAdditionalDocumentsByCandidateIdQuery } from '$store/modules/additional-documents';
import isAudioFile from '$utils/isAudioFile';
import { useCandidateId } from '$hooks/useCandidateId';
import Spinner from '../../../../Spinner/Spinner';
import classes from './AudioGallery.module.css';
import AudioCard from './AudioCard';

/**
 * Audio Files are "Files" rows under the "Audio Assessment" category, so they
 * come from the documents endpoint and are told apart by their MediaType.
 */
export const AudioGallery = () => {
  const id = useCandidateId();
  const [t, i18next] = useTranslation('global');

  const { data: additionalDocuments, isLoading } =
    useGetAdditionalDocumentsByCandidateIdQuery({
      candidateId: id,
      lang: i18next.language as 'en' | 'es',
    });

  const audioFiles = useMemo(() => {
    return (additionalDocuments || []).filter(isAudioFile);
  }, [additionalDocuments]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', width: '100%', alignContent: 'center' }}>
        <Spinner height="200px" />
      </div>
    );
  }

  return (
    <section data-testid="audio-gallery">
      <p className={classes.title}>
        {t('AdditionalVideos.audio.galleryTitleInternal')}
      </p>
      {audioFiles.length === 0 ? (
        <div className={classes.empty}>
          <h3>{t('AdditionalVideos.audio.noAudioTitle')}</h3>
          <p className={classes.emptyText}>
            {t('AdditionalVideos.audio.noAudioText')}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {audioFiles.map((singleAudio) => (
            <AudioCard key={singleAudio.Id} audio={singleAudio} />
          ))}
        </div>
      )}
    </section>
  );
};
